import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { useLazyDataLoader } from '@europrocurement/l2d-modules';
import { useTiersOfferContext } from '@europrocurement/l2d-modules/modules/Offres/providers';
import {
    TiersCommercial,
    UPDATE_TIERS_COMMERCIAUX_CTX,
    useTiersCtx,
    useTiersCtxDispatch,
} from '@europrocurement/l2d-modules/providers/TiersProvider';
import { TiersTiersPrescripteurReadCommerciaux } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useTiersService, useSociete } from '@europrocurement/l2d-domain';
import { getSanitizedId } from '../../../../utils/sanitize';
import { useCRMCustomHeader } from '../../providers/CRMCustomHeaderContext';
import { PrescriberHero } from '../../components/PrescriberHero';
import { PrescriberDetailBody } from '../../components/PrescriberDetailBody';

const PrescriberDetailCRM: React.FunctionComponent = function () {
    const { id } = useParams();
    const { loadTiers } = useLazyDataLoader();
    const { getTiersById, isFetch } = useTiersCtx();
    const tiersDispatch = useTiersCtxDispatch();
    const { setIsCRMPrescripteurPath, setOfferName, offerName } = useCRMCustomHeader();
    const { getTiersCurrentOfferWithDefault, offerDataLoaded } = useTiersOfferContext();
    const { prescribersApi } = useTiersService();
    const xIdSociete = useSociete();
    const numericId = getSanitizedId(id);
    const loading = !isFetch(Number(numericId));
    const [loadError, setLoadError] = useState(false);
    const [commerciaux, setCommerciaux] = useState<TiersCommercial[] | undefined>(undefined);

    useEffect(() => {
        setIsCRMPrescripteurPath(true);
        return () => {
            setIsCRMPrescripteurPath(false);
            setOfferName(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (numericId) {
            loadTiers(Number(numericId)).catch(() => setLoadError(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numericId]);

    useEffect(() => {
        if (!numericId || !offerDataLoaded) {
            return;
        }
        getTiersCurrentOfferWithDefault(Number(numericId))
            .then((offer) => {
                setOfferName(offer?.libelle);
            })
            .catch(() => setOfferName(undefined));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numericId, offerDataLoaded]);

    useEffect(() => {
        if (!numericId) {
            return;
        }
        prescribersApi
            .getCommerciauxPrescripteurTiersItem({ id: String(numericId), xIdSociete })
            .then((response) => {
                const data = response.data as TiersTiersPrescripteurReadCommerciaux;
                const mapped: TiersCommercial[] = (data.commerciaux ?? []).map((c) => ({
                    id: c.id as number,
                    actif: c.actif,
                    utilisateur: {
                        id: c.utilisateur?.id,
                        nom: c.utilisateur?.nom,
                        prenom: c.utilisateur?.prenom,
                        initiales: c.utilisateur?.initiales,
                        email: c.utilisateur?.email,
                        portable: c.utilisateur?.portable,
                        ligneDirecte: c.utilisateur?.ligneDirecte,
                    },
                    domaine: c.domaine
                        ? { id: c.domaine.id, libelle: c.domaine.libelle }
                        : undefined,
                }));
                setCommerciaux(mapped);
                if (tiersDispatch) {
                    tiersDispatch({
                        type: UPDATE_TIERS_COMMERCIAUX_CTX,
                        payload: { tiersId: Number(numericId), commerciaux: mapped },
                    });
                }
            })
            .catch((e) => console.error(e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numericId]);

    const prescripteur = getTiersById(Number(numericId));

    if (!numericId) {
        return (
            <Container maxWidth="lg">
                <Typography>Identifiant invalide.</Typography>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box
                    display="flex"
                    justifyContent="center"
                    mt={6}
                >
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container maxWidth="lg">
                <Box mt={4}>
                    <Typography>Erreur lors du chargement du prescripteur.</Typography>
                </Box>
            </Container>
        );
    }

    if (!prescripteur) {
        return (
            <Container maxWidth="lg">
                <Box mt={4}>
                    <Typography>Prescripteur introuvable.</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box mt={3}>
                <PrescriberHero
                    prescripteur={prescripteur}
                    offerName={offerName}
                    commerciaux={commerciaux}
                />
            </Box>
            <Box mt={3}>
                <PrescriberDetailBody />
            </Box>
        </Container>
    );
};

export default PrescriberDetailCRM;
